import clsx from 'clsx';
import { isEqual } from 'lodash';
import { Fragment, useEffect, useState } from 'react';
import type { FloatingContext } from '@floating-ui/react';

import { useFilterTagCleanup } from '@/hooks/use-filter-tag-cleanup';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Tooltip } from '@flintmint/web-shared/components/tooltip';
import { Popover } from '@flintmint/web-shared/components/popover';
import { Divider } from '@flintmint/web-shared/components/divider';
import { Button } from '@flintmint/web-shared/components/button';
import { TagFilter } from '@/components/filters/parts/tag-filter';
import { DatePicker } from '@flintmint/web-shared/components/date-picker';
import { normalizeDateRangeValueOnApply, type DateRangeValue } from '@flintmint/web-shared/components/date-picker';

import { conditionalSpread } from '@flintmint/web-shared/utils/helper-utils';

import styles from './filters.module.scss';

const EMPTY_FILTERS: FiltersValue = {
  includedTagIds: [],
  excludedTagIds: [],
};
const EMPTY_DATE_FILTER_VALUE: DateRangeValue = {
  preset: null,
  range: [null, null],
};

export const Filters = ({ value, onChange }: FilterProps) => {
  const showDateFilter = 'date' in value;

  const [open, setOpen] = useState(false);
  const [draftValue, setDraftValue] = useState(value);
  const [activeFilter, setActiveFilter] = useState<FilterType>('tags');

  useEffect(() => {
    if (!open) {
      setDraftValue(value);
    }
  }, [open, value]);

  useFilterTagCleanup({
    value,
    onChange,
    draftValue,
    setDraftValue,
  });

  const hasDateSelection = (value?: DateRangeValue | null) => {
    const [start, end] = value?.range ?? [];
    return !!value && (value.preset || !!start || end);
  };

  const hasDraftChanges = !isEqual(draftValue, value);
  const hasDraftTagChanges = !!draftValue.includedTagIds.length || !!draftValue.excludedTagIds.length;
  const hasAnyDraftFilters = hasDraftTagChanges || hasDateSelection(draftValue.date);

  const updateDraft = (patch: Partial<FiltersValue>) => {
    setDraftValue((prev) => ({
      ...prev,
      ...patch,
    }));
  };

  const clearTags = () => {
    updateDraft({
      includedTagIds: [],
      excludedTagIds: [],
    });
  };

  const onClose = () => {
    // Small delay before closing to avoid abrupt UI jump when filters apply and results update immediately
    setTimeout(() => setOpen(false));
  };

  const clearAll = () => {
    onChange({
      ...EMPTY_FILTERS,
      ...conditionalSpread({ date: EMPTY_DATE_FILTER_VALUE }, showDateFilter),
    });
    onClose();
  };

  const apply = () => {
    const next =
      showDateFilter && draftValue.date
        ? {
            ...draftValue,
            date: normalizeDateRangeValueOnApply(draftValue.date),
          }
        : draftValue;
    onChange(next);
    onClose();
  };

  const filterItems: FilterItem[] = [
    {
      id: 'tags',
      icon: 'tags',
      label: 'Filter by tags',
      tooltip: 'Clear selected tags',
      appliedFiltersCount: draftValue.includedTagIds.length + draftValue.excludedTagIds.length,
      onClear: clearTags,
    },
    ...conditionalSpread(
      [
        {
          id: 'date',
          icon: 'calendar-03',
          label: 'Filter by date',
          tooltip: 'Clear selected date',
          appliedFiltersCount: hasDateSelection(draftValue.date) ? 1 : 0,
          onClear: () => updateDraft({ date: EMPTY_DATE_FILTER_VALUE }),
        },
      ],
      showDateFilter
    ),
  ];

  const renderFilterContent = (context: FloatingContext) => {
    switch (activeFilter) {
      case 'tags':
        return (
          <TagFilter
            context={context}
            includedTagIds={draftValue.includedTagIds}
            excludedTagIds={draftValue.excludedTagIds}
            setIncludedTagIds={(includedTagIds) => updateDraft({ includedTagIds })}
            setExcludedTagIds={(excludedTagIds) => updateDraft({ excludedTagIds })}
          />
        );
      case 'date':
        return (
          <DatePicker
            value={draftValue.date ?? EMPTY_DATE_FILTER_VALUE}
            onChange={(date) =>
              updateDraft({
                date,
              })
            }
          />
        );
      default:
        return null;
    }
  };

  return (
    <Popover
      open={open}
      onClose={onClose}
      content={({ context }) => (
        <div className={styles.filtersHolder}>
          <ul className={styles.body}>
            <li className={styles.filterMenuItems}>
              {filterItems.map((filterItem, index) => {
                const { id, onClear, tooltip, appliedFiltersCount, icon, label } = filterItem;
                const hasAppliedFilters = !!appliedFiltersCount;
                return (
                  <Fragment key={id}>
                    <div
                      className={clsx(styles.filterMenuItem, {
                        [styles.active]: activeFilter === id,
                      })}
                      onClick={() => setActiveFilter(id)}
                    >
                      <div>
                        <Icon type={icon} size={20} />
                        <span>{label}</span>
                        {hasAppliedFilters &&
                          (id === 'date' ? (
                            <Icon type="checkmark-circle-02" size={16} className={styles.appliedDateFilter} />
                          ) : (
                            <small className="badge">{appliedFiltersCount}</small>
                          ))}
                      </div>
                      {hasAppliedFilters && (
                        <Tooltip content={tooltip}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onClear();
                            }}
                            data-testid={`clear-${id}`}
                            className={styles.clearBtn}
                          >
                            <Icon type="cancel-circle" size={20} />
                          </button>
                        </Tooltip>
                      )}
                    </div>
                    {filterItems.length - 1 !== index && (
                      <div className={styles.dividerHolder}>
                        <Divider orientation="horizontal" size="100%" />
                      </div>
                    )}
                  </Fragment>
                );
              })}
            </li>
            <li className={styles.filterContent}>{renderFilterContent(context)}</li>
          </ul>
          <ul>
            <li />
            <li className={styles.filterActions}>
              <Button
                cornerRadius="smooth"
                appearance="clear"
                disabled={!hasAnyDraftFilters}
                onClick={clearAll}
                data-testid="filters-clear-all-btn"
              >
                Clear All
              </Button>

              <Button cornerRadius="smooth" disabled={!hasDraftChanges} onClick={apply} data-testid="filters-apply-btn">
                Apply
              </Button>
            </li>
          </ul>
        </div>
      )}
    >
      <button
        onClick={() => setOpen(true)}
        className={clsx(styles.filterIcon, {
          [styles.hasAppliedFilters]: value.includedTagIds.length || value.excludedTagIds.length || hasDateSelection(value.date),
        })}
        data-testid="filter-icon"
      >
        <Icon type="filter-1" size={20} />
      </button>
    </Popover>
  );
};

type FilterType = 'tags' | 'date';

interface FiltersValue {
  includedTagIds: string[];
  excludedTagIds: string[];
  date?: DateRangeValue | null;
}

type FilterItem = {
  id: FilterType;
  icon: string;
  label: string;
  tooltip: string;
  onClear: () => void;
  appliedFiltersCount?: number;
};

interface FilterProps {
  value: FiltersValue;
  onChange: (value: FiltersValue) => void;
}
